import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { kg, money } from "@/lib/calc";
import { shiftLabel, lineEmoji } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { exportKPI, exportWeekly, exportDaily, exportWorkerEarnings, exportWorkerPerformance } from "@/lib/excel";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, ChevronRight, Download, Lock, Table2, AlertTriangle, Trash2, Image } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dow = (iso) => new Date(iso + "T12:00:00").getDay();

export default function Reports() {
  const { canSeeMoney, user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [clearOpen, setClearOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);
  const [embedPhotos, setEmbedPhotos] = useState(false);
  // Wrap every export in a progress toast so users see the big spreadsheet is being built.
  const runExport = (label, fn) => {
    toast.promise(Promise.resolve().then(fn), {
      loading: `Preparing ${label}… please wait${embedPhotos ? " (embedding photos — this can take a moment)" : ""}`,
      success: `${label} downloaded ✓`,
      error: `Couldn't build ${label} — please try again`,
    });
  };
  const doClear = async () => {
    setClearing(true);
    try {
      const r = await api.delete(`/entries/week/${week}`);
      toast.success(`Cleared ${r.data.deleted} shift${r.data.deleted === 1 ? "" : "s"} for Week ${week}`);
      setClearOpen(false); setConfirmText(""); load(week);
    } catch (e) { toast.error("Could not clear — admin only"); }
    finally { setClearing(false); }
  };
  const [kpi, setKpi] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState([]);
  const [week, setWeek] = useState(null);
  const [shiftFilter, setShiftFilter] = useState("all");
  const load = async (wk) => {
    // Resolve the week first (kpi returns the current week when none is passed),
    // then always scope entries + stats to that single week so exports never mix weeks.
    const r = await api.get("/kpi", { params: wk != null ? { week: wk } : {} });
    const w = wk != null ? wk : r.data.week;
    setKpi(r.data); setWeek(w);
    const params = { week: w };
    api.get("/entries", { params }).then((res) => setEntries(res.data));
    api.get("/workers/stats", { params }).then((res) => setStats(res.data.workers || []));
  };
  useEffect(() => { load(); }, []);

  // Sunday→Saturday dates of the loaded week (from earliest entry, else today)
  const dates = useMemo(() => {
    const all = entries.map((e) => e.date).filter(Boolean).sort();
    const base = all[0] || new Date().toISOString().slice(0, 10);
    const d = new Date(base + "T12:00:00"); d.setDate(d.getDate() - d.getDay());
    return Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return x.toISOString().slice(0, 10); });
  }, [entries]);

  const dayKg = (iso) => entries.filter((e) => e.date === iso).reduce((s, e) => s + (Number(e.total_worked) || 0), 0);

  // Shift filter scopes the entry-derived tables below (product/line tally + shifts-filed list).
  const fentries = useMemo(() => entries.filter((e) => shiftFilter === "all" || e.shift === shiftFilter), [entries, shiftFilter]);

  // product tally (single pass over entries — value/unpriced only present for managers)
  const productRows = useMemo(() => {
    const by = new Map();
    fentries.forEach((e) => (e.allocations || []).forEach((a) => {
      if (!by.has(a.code)) by.set(a.code, { code: a.code, kg: 0, value: 0, unpriced: false });
      const r = by.get(a.code);
      r.kg += Number(a.kg) || 0;
      if (a.value != null) r.value += Number(a.value) || 0;
      if (a.unpriced) r.unpriced = true;
    }));
    return [...by.values()].sort((a, b) => b.kg - a.kg);
  }, [fentries]);

  // Line 1 (tally only, not pooled) vs Line 2 (pooled + tallied) kilos this week
  const lineClass = useMemo(() => ({
    l1: entries.reduce((a, e) => a + (Number(e.line1_kg) || 0), 0),
    l2: entries.reduce((a, e) => a + (Number(e.line2_kg) || 0), 0),
  }), [entries]);
  const downtimeTotal = useMemo(() => entries.reduce((a, e) => a + (e.workers || []).reduce((s, w) => s + (Number(w.downtime_min) || 0), 0), 0), [entries]);
  const trainingTotal = useMemo(() => entries.reduce((a, e) => a + (e.training || []).reduce((s, t) => s + (Number(t.minutes) || 0), 0), 0), [entries]);
  const lineRows = useMemo(() => {
    const by = new Map();
    fentries.forEach((e) => {
      const l = e.line || "—";
      if (!by.has(l)) by.set(l, { line: l, kg: 0, value: 0 });
      const r = by.get(l);
      r.kg += Number(e.total_worked) || 0;
      r.value += Number(e.production_value) || 0;
    });
    return [...by.values()].sort((a, b) => b.kg - a.kg);
  }, [fentries]);

  // Kilos by shift — computed from the FULL week so the TOTAL row reconciles with Total kg.
  const shiftRows = useMemo(() => {
    const by = new Map();
    entries.forEach((e) => {
      const k = e.shift || "—";
      if (!by.has(k)) by.set(k, { shift: k, kg: 0, value: 0, count: 0 });
      const r = by.get(k);
      r.kg += Number(e.total_worked) || 0;
      r.value += Number(e.production_value) || 0;
      r.count += 1;
    });
    const idx = (s) => { const i = SHIFT_ORDER.indexOf(s); return i < 0 ? 99 : i; };
    return [...by.values()].sort((a, b) => idx(a.shift) - idx(b.shift));
  }, [entries]);
  const shiftTotal = useMemo(() => shiftRows.reduce((a, r) => ({ kg: a.kg + r.kg, value: a.value + r.value, count: a.count + r.count }), { kg: 0, value: 0, count: 0 }), [shiftRows]);

  if (!kpi) return <div className="p-6 text-slate-800">Loading…</div>;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-2">
        <div><h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800">Reports</h1><p className="text-slate-500 text-sm">{canSeeMoney ? "KPI & production value" : "Production kilos"}</p></div>
        <div className="flex items-center gap-1 bg-white rounded-full border border-slate-200 shadow-sm">
          <button data-testid="r-week-prev" onClick={() => load(week - 1)} className="p-2.5 text-teal-600"><ChevronLeft size={18} strokeWidth={2.5} /></button>
          <span className="font-num font-semibold text-slate-800 px-1 text-sm">Week {week}</span>
          <button data-testid="r-week-next" onClick={() => load(week + 1)} className="p-2.5 text-teal-600"><ChevronRight size={18} strokeWidth={2.5} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total kg" value={kg(kpi.total_kg)} />
        <Kpi label="Shifts" value={kpi.shifts} />
        {canSeeMoney ? <>
          <Kpi label="Production value" value={money(kpi.production_value)} accent />
          <Kpi label="Avg $/kg (priced)" value={money(kpi.avg_per_kg)} accent />
        </> : (
          <div className="col-span-2 rounded-2xl bg-slate-100 p-3 flex items-center gap-2 text-slate-500 text-sm"><Lock size={16} /> Managers only 🔒</div>
        )}
      </div>
      {canSeeMoney && kpi.unpriced_kg > 0 && (
        <div data-testid="unpriced-note" className="flex items-center gap-2 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2">
          <AlertTriangle size={16} /> {kg(kpi.unpriced_kg)} kg produced has no price set — excluded from Avg $/kg. Set prices in Admin › Pricing.
        </div>
      )}

      {/* Shift filter — tap a shift to scope the tables & see each shift's kilos */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3" data-testid="shift-filter-card">
        <div className="text-xs font-semibold text-slate-500 mb-2">Filter by shift — tap to see each shift's kilos (scopes the tables below)</div>
        <ShiftChips rows={shiftRows} total={shiftTotal.kg} value={shiftFilter} onChange={setShiftFilter} />
      </div>

      {/* Export row */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><Download size={16} strokeWidth={2.5} /> Export</div>
          <div className="flex gap-2">
            <Button data-testid="export-weekly" onClick={() => runExport(`Weekly (Week ${week})`, () => exportWeekly(entries, week, canSeeMoney, embedPhotos))} className="gap-1.5 btn-grad text-white rounded-full h-10 text-xs"><Download size={14} strokeWidth={2.5} /> Weekly</Button>
            <Button data-testid="export-worker-performance" onClick={() => runExport(`Worker performance (Week ${week})`, () => exportWorkerPerformance(entries, week, canSeeMoney))} variant="outline" className="gap-1.5 rounded-full border-teal-200 text-teal-700 h-10 text-xs"><Download size={14} strokeWidth={2.5} /> Worker sheets</Button>
            {canSeeMoney && <Button data-testid="export-earnings" onClick={() => runExport("Worker earnings", () => exportWorkerEarnings(entries, week))} className="gap-1.5 rounded-full h-10 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"><Download size={14} strokeWidth={2.5} /> Worker earnings</Button>}
            {canSeeMoney && <Button data-testid="export-kpi" onClick={() => runExport("KPI", () => exportKPI(kpi, week))} variant="outline" className="gap-1.5 rounded-full border-teal-200 text-teal-700 h-10 text-xs"><Download size={14} strokeWidth={2.5} /> KPI</Button>}
            {isAdmin && <Button data-testid="clear-week-btn" onClick={() => setClearOpen(true)} variant="outline" className="gap-1.5 rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 h-10 text-xs"><Trash2 size={14} strokeWidth={2.5} /> Clear week</Button>}
          </div>
        </div>
        <label data-testid="embed-photos-toggle" className="flex items-center gap-2 text-xs text-slate-600 mb-2 cursor-pointer select-none">
          <Checkbox checked={embedPhotos} onCheckedChange={(v) => setEmbedPhotos(!!v)} />
          <Image size={14} className="text-slate-400" strokeWidth={2.5} />
          <span>{embedPhotos ? <b className="text-slate-700">Full file — photos embedded (larger download)</b> : "Light file — photos as clickable links (default, small). Tick to embed full photos."}</span>
        </label>
        <div className="text-xs text-slate-400 mb-2">One-tap Daily xlsx per day</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {dates.map((d, i) => {
            const k = dayKg(d);
            return (
              <button key={d} data-testid={`export-day-${i}`} disabled={!k} onClick={() => runExport(`${DAYS[dow(d)]} daily`, () => exportDaily(entries, d, canSeeMoney, embedPhotos))}
                className={`rounded-xl border p-2 text-left transition-colors ${k ? "border-teal-200 bg-teal-50/50 hover:bg-teal-50 text-teal-800" : "border-slate-100 bg-slate-50 text-slate-300"}`}>
                <div className="flex items-center justify-between text-xs font-semibold">{DAYS[dow(d)]} <Download size={13} strokeWidth={2.5} /></div>
                <div className="font-num text-[11px] mt-0.5">{k ? `${kg(k)} kg` : "—"}</div>
              </button>
            );
          })}
        </div>
      </div>
      {canSeeMoney && <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1"><Lock size={12} /> KPI only — never payroll</div>}

      <Dialog open={clearOpen} onOpenChange={(o) => { setClearOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-rose-600 flex items-center gap-2"><Trash2 size={18} strokeWidth={2.5} /> Clear Week {week}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This permanently deletes <b>all {entries.length} filed shift{entries.length === 1 ? "" : "s"}</b> (every worker's kilos) for <b>Week {week}</b>. This cannot be undone.</p>
          <p className="text-xs text-slate-500 mt-1">Type <b className="font-num">CLEAR</b> to confirm:</p>
          <Input data-testid="clear-week-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="CLEAR" className="h-11" />
          <div className="flex gap-2 justify-end mt-1">
            <Button variant="outline" onClick={() => setClearOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="clear-week-confirm" disabled={confirmText !== "CLEAR" || clearing} onClick={doClear} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40">{clearing ? "Clearing…" : "Delete all kilos"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shifts filed this week — who logged each entry */}
      {entries.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="px-4 py-3 flex items-center gap-1.5 font-heading font-bold text-slate-800"><Table2 size={18} strokeWidth={2.5} /> Shifts filed this week</div>
          <div className="divide-y divide-slate-100">
            {fentries.map((e) => (
              <div key={e.id} data-testid={`report-shift-${e.id}`} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{e.line} · {shiftLabel(e.shift)}</div>
                  <div className="text-[11px] text-slate-400">{e.date} · Logged by <span className="font-medium text-slate-600">{e.saved_by_name || "—"}</span></div>
                </div>
                <div className="font-num text-sm text-slate-700 whitespace-nowrap">{kg(e.total_worked)} kg</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-worker weekly table — SINGLE SOURCE: /workers/stats (same as Workers page) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-4 py-3 flex items-center gap-1.5 font-heading font-bold text-slate-800"><Table2 size={18} strokeWidth={2.5} /> Per-worker earned kg (Sun → Sat)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="worker-table">
            <thead>
              <tr className="btn-grad text-white text-left">
                <th className="sticky left-0 btn-grad px-3 py-2.5 font-semibold whitespace-nowrap">Worker</th>
                {dates.map((d, i) => <th key={i} className="px-2 py-2.5 font-semibold text-right whitespace-nowrap">{DAYS[dow(d)]}</th>)}
                <th className="px-2 py-2.5 font-semibold text-right">Min</th>
                <th className="px-2 py-2.5 font-semibold text-right">Bkts</th>
                <th className="px-2 py-2.5 font-semibold text-right">Weighed</th>
                <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">Week kg</th>
                {canSeeMoney && <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap">$ Earned</th>}
              </tr>
            </thead>
            <tbody>
              {stats.length === 0 && <tr><td colSpan={12} className="text-center text-slate-400 py-8">No worker activity this week.</td></tr>}
              {stats.map((r) => (
                <tr key={r.worker_id} data-testid={`wt-row-${r.name}`} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.number != null ? <span className="text-slate-400">#{r.number} </span> : null}{r.name}</td>
                  {(r.by_day || [0,0,0,0,0,0,0]).map((k, i) => <td key={i} className="px-2 py-2 text-right font-num text-slate-600">{k ? kg(k) : "–"}</td>)}
                  <td className="px-2 py-2 text-right font-num text-slate-600">{r.minutes || "–"}</td>
                  <td className="px-2 py-2 text-right font-num text-slate-600">{r.buckets || "–"}</td>
                  <td className="px-2 py-2 text-right font-num text-slate-600">{r.weighed_kg ? kg(r.weighed_kg) : "–"}</td>
                  <td className="px-3 py-2 text-right font-num font-semibold text-slate-800">{kg(r.earned_kg)}</td>
                  {canSeeMoney && <td className="px-3 py-2 text-right font-num font-semibold text-emerald-700">{money(r.value || 0)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(downtimeTotal > 0 || trainingTotal > 0) && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 grid grid-cols-2 gap-3" data-testid="dt-tally">
          <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs font-medium text-slate-500">Downtime (recorded)</div><div className="font-num font-semibold text-xl text-slate-800" data-testid="downtime-total">{downtimeTotal} min</div></div>
          <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs font-medium text-slate-500">Training time (recorded)</div><div className="font-num font-semibold text-xl text-slate-800" data-testid="training-total">{trainingTotal} min</div></div>
        </div>
      )}

      {(lineClass.l1 > 0 || lineClass.l2 > 0) && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 grid grid-cols-2 gap-3" data-testid="line12-tally">
          <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs font-medium text-slate-500">Line 1 — tally only (not pooled)</div><div className="font-num font-semibold text-xl text-slate-800" data-testid="line1-total">{kg(lineClass.l1)} kg</div></div>
          <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs font-medium text-slate-500">Line 2 — pooled + tallied</div><div className="font-num font-semibold text-xl text-slate-800" data-testid="line2-total">{kg(lineClass.l2)} kg</div></div>
        </div>
      )}

      {/* Kilos by line table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-4 py-3 flex items-center gap-1.5 font-heading font-bold text-slate-800"><Table2 size={18} strokeWidth={2.5} /> Kilos by line {canSeeMoney ? "& value" : ""}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="line-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left">
                <th className="sticky left-0 bg-slate-50 px-3 py-2.5 font-semibold">Line</th>
                <th className="px-3 py-2.5 font-semibold text-right">Week kg</th>
                {canSeeMoney && <th className="px-3 py-2.5 font-semibold text-right">Value $</th>}
              </tr>
            </thead>
            <tbody>
              {lineRows.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-8">No production this week.</td></tr>}
              {lineRows.map((r) => (
                <tr key={r.line} data-testid={`lt-row-${r.line}`} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white px-3 py-2 text-slate-800 whitespace-nowrap font-medium">{lineEmoji(r.line)} {r.line}</td>
                  <td className="px-3 py-2 text-right font-num font-semibold text-slate-800">{kg(r.kg)}</td>
                  {canSeeMoney && <td className="px-3 py-2 text-right font-num text-teal-700">{money(r.value)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Kilos by shift table — reconciles to Total kg */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-4 py-3 flex items-center gap-1.5 font-heading font-bold text-slate-800"><Table2 size={18} strokeWidth={2.5} /> Kilos by shift {canSeeMoney ? "& value" : ""}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="shift-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left">
                <th className="sticky left-0 bg-slate-50 px-3 py-2.5 font-semibold">Shift</th>
                <th className="px-3 py-2.5 font-semibold text-right">Shifts</th>
                <th className="px-3 py-2.5 font-semibold text-right">Week kg</th>
                {canSeeMoney && <th className="px-3 py-2.5 font-semibold text-right">Value $</th>}
              </tr>
            </thead>
            <tbody>
              {shiftRows.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-8">No production this week.</td></tr>}
              {shiftRows.map((r) => (
                <tr key={r.shift} data-testid={`st-row-${r.shift}`} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white px-3 py-2 text-slate-800 whitespace-nowrap font-medium">{shiftLabel(r.shift)}</td>
                  <td className="px-3 py-2 text-right font-num text-slate-600">{r.count}</td>
                  <td className="px-3 py-2 text-right font-num font-semibold text-slate-800">{kg(r.kg)}</td>
                  {canSeeMoney && <td className="px-3 py-2 text-right font-num text-teal-700">{money(r.value)}</td>}
                </tr>
              ))}
              {shiftRows.length > 0 && (
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold" data-testid="st-total">
                  <td className="sticky left-0 bg-slate-50 px-3 py-2 text-slate-800">TOTAL</td>
                  <td className="px-3 py-2 text-right font-num text-slate-800">{shiftTotal.count}</td>
                  <td className="px-3 py-2 text-right font-num text-slate-800">{kg(shiftTotal.kg)}</td>
                  {canSeeMoney && <td className="px-3 py-2 text-right font-num text-teal-700">{money(shiftTotal.value)}</td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product tally table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-4 py-3 flex items-center gap-1.5 font-heading font-bold text-slate-800"><Table2 size={18} strokeWidth={2.5} /> Product tally {canSeeMoney ? "& value" : ""}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="product-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left">
                <th className="sticky left-0 bg-slate-50 px-3 py-2.5 font-semibold">Product</th>
                <th className="px-3 py-2.5 font-semibold text-right">Week kg</th>
                {canSeeMoney && <th className="px-3 py-2.5 font-semibold text-right">Value $</th>}
              </tr>
            </thead>
            <tbody>
              {productRows.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-8">No production this week.</td></tr>}
              {productRows.map((r) => (
                <tr key={r.code} data-testid={`pt-row-${r.code}`} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white px-3 py-2 font-num text-slate-800 whitespace-nowrap">{r.code}</td>
                  <td className="px-3 py-2 text-right font-num font-semibold text-slate-800">{kg(r.kg)}</td>
                  {canSeeMoney && <td className="px-3 py-2 text-right font-num">{r.unpriced ? <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle size={13} /> no price set</span> : <span className="text-teal-700">{money(r.value)}</span>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const SHIFT_ORDER = ["night", "day", "afternoon", "pooraka_day"];
const SHIFT_SHORT = { night: "1st · Night", day: "2nd · Day", afternoon: "3rd · Afternoon", pooraka_day: "Pooraka" };

// Shift filter chips with live per-shift kilos (always shows all shifts for parity).
function ShiftChips({ rows, total, value, onChange }) {
  const byShift = Object.fromEntries(rows.map((r) => [r.shift, r.kg]));
  const pill = (on) => `px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`;
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="shift-filter">
      <button data-testid="shift-filter-all" onClick={() => onChange("all")} className={pill(value === "all")}>All shifts · <span className="font-num">{kg(total)}</span></button>
      {SHIFT_ORDER.map((k) => (
        <button key={k} data-testid={`shift-filter-${k}`} onClick={() => onChange(value === k ? "all" : k)} className={pill(value === k)}>
          {SHIFT_SHORT[k]} · <span className="font-num">{kg(byShift[k] || 0)}</span>
        </button>
      ))}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return <div className={`rounded-2xl p-3 border ${accent ? "btn-grad text-white border-teal-600" : "bg-white border-slate-100"}`}>
    <div className={`text-xs font-medium ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</div>
    <div className={`font-num font-semibold text-xl ${accent ? "text-white" : "text-slate-800"}`}>{value}</div>
  </div>;
}
